// API service for communicating with Cloudflare Worker
import type { GymCheckin, ExerciseLog, WorkoutStats, CustomExercise, ApiResponse, ProgressPhoto, PhotoListResponse } from '@/types';
import { supabase } from '@/lib/supabase';
import { getUserTimezone } from '@/utils/timezone';

// Always use the production backend URL unless explicitly in development
const isDevelopment = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.port === '3000';

const API_BASE = isDevelopment 
  ? '/api'  // Use proxy in development
  : 'https://altitude.a-3b3ziz.workers.dev/api';  // Always use production backend

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  // Get current session token or guest ID
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  // Check for guest mode
  const guestId = localStorage.getItem('guestId');

  // Ensure we have some form of authentication
  if (!token && !guestId) {
    return {
      success: false,
      error: 'No authentication available. Please sign in or continue as guest.',
    };
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : `Bearer ${guestId}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Gym check-ins API
export const checkinApi = {
  async getCheckins(): Promise<ApiResponse<GymCheckin[]>> {
    return apiRequest('/checkins');
  },

  async getCheckinStatus(): Promise<ApiResponse<{ isCheckedIn: boolean; checkinInfo?: { timestamp: string; date: string } }>> {
    return apiRequest('/checkins/status');
  },

  async createCheckin(): Promise<ApiResponse<{ message: string }>> {
    return apiRequest('/checkins', { method: 'POST' });
  },

  // For testing - remove today's check-in
  async deleteTodaysCheckin(): Promise<ApiResponse<{ message: string }>> {
    const today = new Date().toISOString().split('T')[0];
    return apiRequest(`/checkins/${today}`, { method: 'DELETE' });
  },
};

// Daily commitment API
export const commitmentApi = {
  async getTodayCommitment(): Promise<ApiResponse<{ committed: boolean; completed: boolean; date: string }>> {
    return apiRequest('/commitment/today');
  },

  async createCommitment(data: { committed: boolean }): Promise<ApiResponse<{ committed: boolean; date: string; message: string }>> {
    return apiRequest('/commitment/today', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Exercise logs API
export const exerciseApi = {
  async getExercises(params?: { date?: string; exercise?: string }): Promise<ApiResponse<ExerciseLog[]>> {
    const searchParams = new URLSearchParams();
    if (params?.date) searchParams.set('date', params.date);
    if (params?.exercise) searchParams.set('exercise', params.exercise);

    const query = searchParams.toString();
    return apiRequest(`/exercises${query ? `?${query}` : ''}`);
  },

  async createExercise(data: {
    exercise: string;
    weight: number;
    reps: number;
    sets: number;
    notes?: string;
  }): Promise<ApiResponse<{ isPersonalRecord: boolean; message: string }>> {
    return apiRequest('/exercises', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteExercise(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/exercises/${id}`, { method: 'DELETE' });
  },
};

// Custom exercises API
export const customExerciseApi = {
  async getCustomExercises(): Promise<ApiResponse<CustomExercise[]>> {
    return apiRequest('/custom-exercises');
  },

  async createCustomExercise(data: {
    name: string;
    category?: string;
  }): Promise<ApiResponse<{ id: string; name: string; user_input: string; category: string; muscle_groups: any; message?: string }>> {
    return apiRequest('/custom-exercises', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteCustomExercise(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/custom-exercises/${id}`, { method: 'DELETE' });
  },
};

// Statistics API
export const statsApi = {
  async getStats(): Promise<ApiResponse<WorkoutStats>> {
    return apiRequest('/stats');
  },
};

// Photos API
export const photoApi = {
  async getPhotos(params?: {
    limit?: number;
    cursor?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PhotoListResponse>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);

    const query = searchParams.toString();
    return apiRequest(`/photos${query ? `?${query}` : ''}`);
  },

  async getPhoto(id: string): Promise<ApiResponse<ProgressPhoto>> {
    return apiRequest(`/photos/${id}`);
  },

  async updatePhoto(id: string, data: {
    notes?: string;
    photoDate?: string;
  }): Promise<ApiResponse<ProgressPhoto>> {
    return apiRequest(`/photos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deletePhoto(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/photos/${id}`, { method: 'DELETE' });
  },
};

// User management
export const userApi = {
  async createUser(): Promise<ApiResponse<{ id: string; userId: string }>> {
    // Include timezone when creating user
    const timezone = getUserTimezone();
    return apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify({ timezone }),
    });
  },

  async updateTimezone(timezone: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest('/users/timezone', {
      method: 'PUT',
      body: JSON.stringify({ timezone }),
    });
  },
};

// Gap Analysis
export const analysisApi = {
  async getGapAnalysis(): Promise<ApiResponse<{
    insights: Array<{
      title: string;
      description: string;
      recommendation: string;
      priority: 'high' | 'medium' | 'low';
    }>;
    summary: string;
    analyzed_at: string;
    data_summary: {
      workouts_analyzed: number;
      days_covered: number;
      muscle_groups_tracked: number;
    };
    cached?: boolean;
  }>> {
    return apiRequest('/analysis/gaps', {
      method: 'POST',
    });
  },
};